import React from "react";
import { Button, Space, Table, Tag, Typography } from "antd";
import { EyeOutlined, FilePdfOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { ITransactions } from "src/types/types";
import { currencyFmt, safeDate } from "./helpers";
import { useLanguage } from "src/providers/LanguageProvider";

const { Text } = Typography;

type Props = {
  data: ITransactions[];
  loading?: boolean;
  onView: (r: ITransactions) => void;
  onPdf?: (r: ITransactions) => void;
};

export default function TransactionsTable({ data, loading, onView, onPdf }: Props) {
  const { t } = useLanguage();

  const columns: ColumnsType<ITransactions> = [
    {
      title: t("TX_COL_DOC_NO"),
      dataIndex: "DocumentNumber",
      key: "doc",
      width: 200,
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || "-"}</Text>
          {r.RelationName ? (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: r.RelationName }}>
              {r.RelationName}
            </Text>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>{safeDate(r.DocumentDate)}</Text>
        </Space>
      ),
      sorter: (a, b) => (a.DocumentNumber || "").localeCompare(b.DocumentNumber || ""),
    },
    {
      title: t("TX_COL_TOTAL"),
      key: "amounts",
      align: "right",
      width: 220,
      render: (_, r) => (
        <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
          <Text strong>{currencyFmt(r.TotalSell, r.Currency)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("TX_LABEL_PAID")} {currencyFmt(r.PaidAmount, r.Currency)} · {t("TX_LABEL_REMAINING")} {currencyFmt(r.UnpaidAmount, r.Currency)}
          </Text>
        </Space>
      ),
      sorter: (a, b) => Number(a.TotalSell) - Number(b.TotalSell),
    },
    {
      title: t("TX_COL_PAYMENT_STATUS"),
      key: "paymentStatus",
      width: 150,
      align: "center",
      render: (_, r) => {
        const fullyPaid = Number(r.TotalSell || 0) > 0 && Number(r.UnpaidAmount || 0) <= 0;
        return (
          <Tag color={fullyPaid ? "green" : "orange"}>
            {fullyPaid ? t("TX_PAYMENT_PAID") : t("TX_PAYMENT_UNPAID")}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const ap = Number(a.UnpaidAmount || 0) <= 0 ? 1 : 0;
        const bp = Number(b.UnpaidAmount || 0) <= 0 ? 1 : 0;
        return ap - bp;
      },
    },
    {
      title: t("TX_COL_ACTIONS"),
      key: "actions",
      fixed: "right",
      width: 180,
      render: (_, r) => (
        <Space>
          <Button size="middle" icon={<EyeOutlined />} onClick={() => onView(r)}>
            {t("TX_BTN_VIEW")}
          </Button>
          {onPdf && (
            <Button size="middle" icon={<FilePdfOutlined />} onClick={() => onPdf(r)}>
              {t("TX_BTN_PDF")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table<ITransactions>
      rowKey={(r) => String(r.Id)}
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      scroll={{ x: 700 }}
    />
  );
}
